import React, { useEffect, useState, FormEvent } from 'react';
import { tenancyApi } from '../../api';
import type { AdminRole, AdminUserDto } from '../../api/types';
import { useToast } from '../../context/ToastContext';
import { Badge, formatDateTime } from '../../components/ui/Badge';
import { Dialog } from '../../components/ui/Dialog';
import {
  IconPlus,
  IconShield,
  IconSync,
  IconTrash,
} from '../../components/ui/Icons';
import { PaginationBar } from '../../components/ui/PaginationBar';
import { useDebouncedValue, useServerPaginatedQuery } from '../../hooks/usePagination';

export function AdminAdminsPage() {
  const { pushToast } = useToast();

  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebouncedValue(search, 250);
  const trimmedSearch = debouncedSearch.trim() || undefined;

  // Create Admin Dialog
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [role, setRole] = useState<AdminRole>('support');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [googleId, setGoogleId] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);

  const adminsQuery = useServerPaginatedQuery<AdminUserDto>({
    queryKey: `admin-admins:${trimmedSearch ?? ''}`,
    defaultPageSize: 10,
    fetchPage: (p, size) =>
      tenancyApi.getAdminsDataTable({
        page: p,
        page_size: size,
        search: trimmedSearch,
      }),
  });
  const isSearchingAdmins =
    adminsQuery.loading || search.trim() !== debouncedSearch.trim();

  useEffect(() => {
    if (adminsQuery.error) {
      pushToast({
        tone: 'error',
        title: 'Failed to load platform admins',
        description: adminsQuery.error,
      });
    }
  }, [adminsQuery.error, pushToast]);

  const filteredAdmins = adminsQuery.data;

  const handleCreateAdmin = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    try {
      await tenancyApi.createAdmin({
        email: email.trim().toLowerCase(),
        role,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        google_id: googleId.trim() || undefined,
      });
      pushToast({
        tone: 'success',
        title: 'Platform admin created',
        description: `${email.trim()} granted ${role} access.`,
      });
      setCreateOpen(false);
      setEmail('');
      setRole('support');
      setFirstName('');
      setLastName('');
      setGoogleId('');
      await adminsQuery.reload();
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to create admin',
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAdmin = async (record: AdminUserDto) => {
    if (!window.confirm(`Revoke platform administrator access for "${record.email}"?`)) return;
    try {
      await tenancyApi.deleteAdmin(record.id);
      pushToast({
        tone: 'info',
        title: 'Admin access revoked',
        description: record.email,
      });
      await adminsQuery.reload();
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to revoke admin',
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Platform Administrators ({adminsQuery.totalItems})
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Manage Google Workspace operator accounts and role-based access to the Admin Console.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search admin email or role…"
            className="rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs text-slate-900 focus:border-amber-500 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => void adminsQuery.reload()}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <IconSync className="h-3.5 w-3.5" />
            <span>Refresh</span>
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-amber-500 px-4 py-2 text-xs font-semibold text-slate-950 shadow-2xs hover:bg-amber-400"
          >
            <IconPlus className="h-3.5 w-3.5" />
            <span>Add Administrator</span>
          </button>
        </div>
      </div>

      {/* Admins Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xs">
        <div className="table-scroll-container">
          <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
            <thead className="bg-slate-50 font-semibold uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-5 py-3.5">Administrator</th>
                <th className="px-4 py-3.5">Email</th>
                <th className="px-4 py-3.5">Role</th>
                <th className="px-4 py-3.5">Google ID</th>
                <th className="px-4 py-3.5">Created</th>
                <th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isSearchingAdmins ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    Loading platform administrators…
                  </td>
                </tr>
              ) : filteredAdmins.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    No administrators found.
                  </td>
                </tr>
              ) : (
                filteredAdmins.map((record) => {
                  const fullName =
                    [record.first_name, record.last_name].filter(Boolean).join(' ') || 'Operator';
                  return (
                    <tr key={record.id} className="hover:bg-slate-50">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/15 text-amber-700">
                            <IconShield className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900">{fullName}</div>
                            <div className="font-mono text-[10px] text-slate-400">{record.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 font-medium text-slate-700">{record.email}</td>
                      <td className="px-4 py-4">
                        <Badge
                          variant={
                            record.role === 'super_admin'
                              ? 'warning'
                              : record.role === 'support'
                                ? 'info'
                                : 'neutral'
                          }
                          dot
                        >
                          {record.role}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 font-mono text-xs text-slate-500">
                        {record.google_id ?? '—'}
                      </td>
                      <td className="px-4 py-4 text-slate-500">{formatDateTime(record.created_at)}</td>
                      <td className="px-5 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => void handleDeleteAdmin(record)}
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-2.5 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50"
                        >
                          <IconTrash className="h-3.5 w-3.5" />
                          <span>Revoke</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <PaginationBar pagination={adminsQuery} itemLabel="administrators" />
      </div>

      {/* Add Administrator Modal */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add Platform Administrator"
        subtitle="Authorize a staff member to sign in to the Admin Console via Google Workspace OAuth."
      >
        <form onSubmit={handleCreateAdmin} className="space-y-4">
          <div>
            <label
              htmlFor="admin-email-input"
              className="block text-xs font-semibold uppercase tracking-wider text-slate-600"
            >
              Google Workspace Email *
            </label>
            <input
              id="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="operator@sync-platform.io"
              className="mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="admin-first-name"
                className="block text-xs font-semibold uppercase tracking-wider text-slate-600"
              >
                First Name
              </label>
              <input
                id="admin-first-name"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jane"
                className="mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
              />
            </div>
            <div>
              <label
                htmlFor="admin-last-name"
                className="block text-xs font-semibold uppercase tracking-wider text-slate-600"
              >
                Last Name
              </label>
              <input
                id="admin-last-name"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Operator"
                className="mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="admin-role-select"
                className="block text-xs font-semibold uppercase tracking-wider text-slate-600"
              >
                Role
              </label>
              <select
                id="admin-role-select"
                value={role}
                onChange={(e) => setRole(e.target.value as AdminRole)}
                className="mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
              >
                <option value="super_admin">super_admin (Full Control)</option>
                <option value="support">support (Tenant Support &amp; Impersonation)</option>
                <option value="viewer">viewer (Read-Only Audit)</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="admin-google-id"
                className="block text-xs font-semibold uppercase tracking-wider text-slate-600"
              >
                Google Subject ID (Optional)
              </label>
              <input
                id="admin-google-id"
                type="text"
                value={googleId}
                onChange={(e) => setGoogleId(e.target.value)}
                placeholder="Auto-linked on first login"
                className="mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-xs text-slate-900"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !email.trim()}
              className="rounded-lg bg-amber-500 px-4 py-2 text-xs font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50"
            >
              {saving ? 'Adding…' : 'Create Administrator'}
            </button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
